// PlanningActionManager.js - Gestion des actions (TOUTES fonctionnalités - SANS tri)
// Le tri est géré dans PlanningView.js
import React, { useMemo, useState } from 'react';
import { 
  Clock, CheckCircle, Edit, Award, Move, Zap, Trash2, X,
  Timer, Calendar
} from 'lucide-react';
import { Share2 } from 'lucide-react';
import { usePlanningAIActionDetailer, PlanningAIActionDetailerModal } from './PlanningAIActionDetailer';

const PlanningActionManager = ({
  planning,
  currentWeek,
  selectedDay,
  isMobile,
  currentMobileDay,
  editingAction,
  showAddForm,
  showDeleteConfirm,
  actionPlan,
  maxWeeksGenerated,
  currentStepId,
  getDateForDay,
  isDateInPast,
  getWeekStartDate,
  setPlanning,
  setMaxWeeksGenerated,
  setEditingAction,
  setShowAddForm,
  setShowDeleteConfirm,
  savePlanning,
  onCompleteAction,
  onDeleteElementaryAction,
  onUpdateElementaryAction,
  handleShareAction,
  isActionShareable,
  viewMode,
  onGenerateDetailsCallback  // ← NOUVELLE PROP pour exposer la fonction
}) => {
  const [editingPriority, setEditingPriority] = useState(null);

  // Utilisation du hook IA pour la génération d'actions détaillées
  const {
    isGenerating: isGeneratingDetails,
    detailProposals,
    showValidationModal: showDetailValidation,
    generateDetailedActions,
    validateDetailedActions,
    cancelDetailGeneration,
    toggleActionSelection
  } = usePlanningAIActionDetailer({
    planning,
    currentWeek,
    maxWeeksGenerated,
    setPlanning,
    setMaxWeeksGenerated,
    savePlanning,
    getDateForDay,
    isDateInPast,
    getWeekStartDate
  });

  // Fonctions utilitaires
  const isActionToday = (action) => {
    if (!action?.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return action.date === today;
  };

  const isActionFuture = (action) => {
    if (!action?.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return action.date > today;
  };

  // Actions CRUD
  const handleCompleteAction = async (actionId) => {
    const updatedPlanning = [...planning];
    
    // Trouver l'action dans toutes les semaines
    for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
      const weekData = updatedPlanning[weekIndex];
      if (weekData?.elementaryActions) {
        const actionIndex = weekData.elementaryActions.findIndex(a => a.id === actionId);
        if (actionIndex !== -1) {
          weekData.elementaryActions[actionIndex].completed = !weekData.elementaryActions[actionIndex].completed;
          weekData.elementaryActions[actionIndex].completedAt = weekData.elementaryActions[actionIndex].completed 
            ? new Date().toISOString() : null;
          
          await savePlanning(updatedPlanning);
          
          if (onCompleteAction) {
            onCompleteAction(actionId);
          }
          return;
        }
      }
    }
  };

  const deleteAction = async (actionId) => {
    const originalPlanning = [...planning];
    const updatedPlanning = [...planning];
    
    // Trouver l'action dans toutes les semaines
    let foundWeek = null;
    for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
      const weekData = updatedPlanning[weekIndex];
      if (weekData?.elementaryActions) {
        const actionExists = weekData.elementaryActions.find(a => a.id === actionId);
        if (actionExists) {
          foundWeek = weekIndex;
          break;
        }
      }
    }
    
    if (foundWeek !== null) {
      const weekData = updatedPlanning[foundWeek];
      weekData.elementaryActions = weekData.elementaryActions.filter(a => a.id !== actionId);
      
      try {
        const success = await savePlanning(updatedPlanning);
        
        if (success) {
          if (onDeleteElementaryAction) {
            await onDeleteElementaryAction(actionId);
          }
        } else {
          setPlanning(originalPlanning);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setPlanning(originalPlanning);
      }
    }
    setShowDeleteConfirm(null);
  };

  // Gestion de la priorité
  const handleChangePriority = async (actionId, newPriority) => {
    const updatedPlanning = [...planning];
    
    // Trouver l'action dans le planning
    for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
      const weekData = updatedPlanning[weekIndex];
      if (weekData?.elementaryActions) {
        const actionIndex = weekData.elementaryActions.findIndex(a => a.id === actionId);
        if (actionIndex !== -1) {
          weekData.elementaryActions[actionIndex].priority = newPriority;
          weekData.elementaryActions[actionIndex].updatedAt = new Date().toISOString();
          
          await savePlanning(updatedPlanning);
          setEditingPriority(null);
          return;
        }
      }
    }
  };

  // Récupérer toutes les actions (pour trouver celle à détailler)
  const allActions = useMemo(() => {
    const actions = [];
    planning.forEach((week, weekIndex) => {
      if (week?.elementaryActions) {
        week.elementaryActions.forEach(action => {
          actions.push({
            ...action,
            weekNumber: weekIndex + 1
          });
        });
      }
    });
    return actions;
  }, [planning]);

  // Wrapper pour la génération d'actions détaillées
  const handleGenerateDetailedActions = React.useCallback(async (actionId) => {
    console.log('🔥 Génération de détails pour action:', actionId);
    const action = allActions.find(a => a.id === actionId);
    if (action) {
      console.log('✅ Action trouvée, génération en cours...');
      await generateDetailedActions(action);
    } else {
      console.error('❌ Action non trouvée:', actionId);
    }
  }, [allActions, generateDetailedActions]);

  // Exposer la fonction au parent dès que possible (pas dans useEffect)
  React.useEffect(() => {
    if (onGenerateDetailsCallback) {
      console.log('📤 Exposition de la fonction de génération au parent');
      onGenerateDetailsCallback(handleGenerateDetailedActions);
    }
  }, [onGenerateDetailsCallback, handleGenerateDetailedActions]);

  // Composant Carte d'Action (pour référence - non utilisé ici mais peut être exporté)
  const OptimizedActionCard = ({ action, isMobileView = false, showWeekNumber = false }) => {
    const isCurrentStep = action.stepId && currentStepId && String(action.stepId).trim() === String(currentStepId).trim();
    
    return (
      <div 
        className={`p-3 md:p-4 border rounded-lg transition-all ${
          action.completed
            ? 'border-green-300 bg-green-50'
            : isActionToday(action)
            ? 'border-blue-300 bg-blue-50'
            : isActionFuture(action)
            ? 'border-purple-300 bg-purple-50 hover:border-purple-400'
            : 'border-gray-300 bg-gray-100'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              <button
                onClick={() => handleCompleteAction(action.id)}
                className={`mr-3 transition-colors flex-shrink-0 ${
                  action.completed
                    ? 'text-green-600'
                    : isActionToday(action)
                    ? 'text-blue-600 hover:text-blue-700'
                    : isActionFuture(action)
                    ? 'text-purple-600 hover:text-purple-700'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
              >
                <CheckCircle className="h-5 w-5" />
              </button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h4 className={`font-medium truncate ${
                    action.completed ? 'text-green-800 line-through' : 
                    isActionToday(action) ? 'text-blue-800' :
                    isActionFuture(action) ? 'text-purple-800' :
                    'text-gray-600'
                  }`} title={action.title}>
                    {action.title}
                  </h4>
                  {showWeekNumber && (
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded flex-shrink-0">
                      S{action.weekNumber}
                    </span>
                  )}
                  {isCurrentStep && (
                    <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded font-medium">
                      Étape actuelle
                    </span>
                  )}
                </div>
                {action.description && (
                  <p className="text-sm mt-1 text-gray-600 line-clamp-2">
                    {action.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 md:space-x-3 text-xs flex-wrap gap-y-1">
              {showWeekNumber && (
                <span className="text-gray-600">
                  {action.day} {new Date(action.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
              
              {/* Priorité modifiable */}
              {editingPriority === action.id ? (
                <select
                  value={action.priority}
                  onChange={(e) => handleChangePriority(action.id, e.target.value)}
                  onBlur={() => setEditingPriority(null)}
                  autoFocus
                  className="px-2 py-1 rounded text-xs border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="high">Haute</option>
                  <option value="medium">Moyenne</option>
                  <option value="low">Basse</option>
                </select>
              ) : (
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!action.completed) setEditingPriority(action.id);
                  }}
                  className={`px-2 py-1 rounded text-xs ${
                    !action.completed ? 'cursor-pointer hover:opacity-80' : ''
                  } ${
                    action.priority === 'high' ? 'bg-red-100 text-red-700' :
                    action.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}
                  title={!action.completed ? "Cliquer pour modifier la priorité" : ""}
                >
                  {action.priority === 'high' ? 'Haute' : 
                   action.priority === 'medium' ? 'Moyenne' : 'Basse'}
                </span>
              )}
              
              <span className="flex items-center text-gray-600">
                <Clock className="h-3 w-3 mr-1" />
                {action.duration}
              </span>
              
              {action.timeSlot && (
                <span className="flex items-center text-gray-600 hidden md:flex">
                  <Timer className="h-3 w-3 mr-1" />
                  {action.timeSlot}
                </span>
              )}
            </div>

            {action.deliverable && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                <strong>Résultat attendu :</strong> {action.deliverable}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-1 md:space-x-2 ml-2 flex-shrink-0">
            {!action.completed && action.isElementary && (
              <>
                {!action.isDetailed && (
                  <button
                    onClick={() => handleGenerateDetailedActions(action.id)}
                    disabled={isGeneratingDetails}
                    className="p-1 text-gray-400 hover:text-orange-600 transition-colors disabled:opacity-50 action-button"
                    title="Détailler automatiquement"
                  >
                    {isGeneratingDetails ? (
                      <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                    ) : (
                      <Zap className="h-4 w-4" />
                    )}
                  </button>
                )}
                
                {isActionShareable(action) && (
                  <button
                    onClick={() => handleShareAction(action)}
                    className="p-1 text-gray-400 hover:text-indigo-600 transition-colors action-button share-button-highlight"
                    title="Partager cette action"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                )}
                
                <button
                  onClick={() => setEditingAction(action)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors action-button"
                  title="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => setShowDeleteConfirm(action.id)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors action-button"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                
                {!isMobileView && (
                  <div
                    className="p-1 text-gray-400 hover:text-gray-600 cursor-move action-button"
                    title="Glisser pour déplacer"
                  >
                    <Move className="h-4 w-4" />
                  </div>
                )}
              </>
            )}
            
            {action.completed && (
              <div className="flex items-center text-xs text-green-600">
                <Award className="h-3 w-3 mr-1" />
                +{action.priority === 'high' ? '25' : action.priority === 'medium' ? '15' : '10'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Modal de validation des actions détaillées - Utilise le module AI */}
      {showDetailValidation && (
        <PlanningAIActionDetailerModal
          detailProposals={detailProposals}
          onValidate={validateDetailedActions}
          onCancel={cancelDetailGeneration}
          onToggleSelection={toggleActionSelection}
        />
      )}

      {/* Formulaire d'ajout/édition d'action */}
      {(showAddForm || editingAction) && (
        <PlanningActionForm
          action={editingAction}
          isEditing={!!editingAction}
          onSave={async (actionData) => {
            const updatedPlanning = [...planning];
            const weekData = updatedPlanning[currentWeek] || { elementaryActions: [] };
            
            if (editingAction) {
              // Trouver l'action dans toutes les semaines
              for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
                const week = updatedPlanning[weekIndex];
                if (week?.elementaryActions) {
                  const actionIndex = week.elementaryActions.findIndex(a => a.id === editingAction.id);
                  if (actionIndex !== -1) {
                    week.elementaryActions[actionIndex] = {
                      ...week.elementaryActions[actionIndex],
                      ...actionData,
                      updatedAt: new Date().toISOString()
                    };
                    break;
                  }
                }
              }
              setEditingAction(null);
            } else {
              const newAction = {
                ...actionData,
                id: `elem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                completed: false,
                isElementary: true,
                createdAt: new Date().toISOString()
              };
              weekData.elementaryActions.push(newAction);
              updatedPlanning[currentWeek] = weekData;
              setShowAddForm(false);
            }
            
            await savePlanning(updatedPlanning);
          }}
          onCancel={() => {
            setShowAddForm(false);
            setEditingAction(null);
          }}
          actionPlan={actionPlan}
          selectedDay={isMobile ? currentMobileDay : selectedDay}
          currentWeek={currentWeek}
          getDateForDay={getDateForDay}
        />
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cette action ?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteAction(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Composant de formulaire optimisé
const PlanningActionForm = ({ action, isEditing, onSave, onCancel, actionPlan, selectedDay, currentWeek, getDateForDay }) => {
  const [formData, setFormData] = React.useState({
    day: action?.day || ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'][selectedDay],
    duration: action?.duration || '1 heure',
    timeSlot: action?.timeSlot || '09:00-10:00',
    stepId: action?.stepId || '',
    priority: action?.priority || 'medium',
    selectedDate: action?.date || getDateForDay(selectedDay, currentWeek).toISOString().split('T')[0],
    title: action?.title || '',
    description: action?.description || ''
  });

  const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  
  const getSelectedDate = () => {
    return new Date(formData.selectedDate);
  };

  const selectedDate = getSelectedDate();
  const dayOfWeek = dayNames[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1];

  React.useEffect(() => {
    const newDayOfWeek = dayNames[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1];
    if (newDayOfWeek !== formData.day) {
      setFormData(prev => ({ ...prev, day: newDayOfWeek }));
    }
  }, [formData.selectedDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const finalDate = getSelectedDate();
    const finalDayOfWeek = dayNames[finalDate.getDay() === 0 ? 6 : finalDate.getDay() - 1];
    
    const saveData = {
      ...formData,
      day: finalDayOfWeek,
      date: finalDate.toISOString().split('T')[0],
      stepTitle: actionPlan?.steps?.find(s => s.id === formData.stepId)?.titre || (isEditing ? action.stepTitle : ''),
      type: action?.type || 'execution',
      deliverable: action?.deliverable || `Résultat de: ${formData.title}`
    };

    onSave(saveData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] overflow-y-auto p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Modifier l\'action' : 'Nouvelle action'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {isEditing && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-1">Action à modifier :</h4>
            <p className="text-blue-700">{action.title}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5">
          {!isEditing && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre de l'action *
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  required
                  placeholder="Ex: Rechercher les clubs de sport"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnelle)
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  rows={3}
                  placeholder="Décrivez ce que vous devez faire..."
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de planification
            </label>
            <input
              type="date"
              value={formData.selectedDate}
              onChange={(e) => setFormData({ ...formData, selectedDate: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">
              Jour correspondant : {dayOfWeek}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priorité
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée estimée
              </label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
              >
                <option value="15 minutes">15 minutes</option>
                <option value="30 minutes">30 minutes</option>
                <option value="45 minutes">45 minutes</option>
                <option value="1 heure">1 heure</option>
                <option value="1h30">1h30</option>
                <option value="2 heures">2 heures</option>
                <option value="3 heures">3 heures</option>
                <option value="4 heures">4 heures</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Créneau horaire
            </label>
            <select
              value={formData.timeSlot}
              onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
            >
              <option value="07:00-08:00">07:00-08:00</option>
              <option value="08:00-09:00">08:00-09:00</option>
              <option value="09:00-10:00">09:00-10:00</option>
              <option value="10:00-11:00">10:00-11:00</option>
              <option value="11:00-12:00">11:00-12:00</option>
              <option value="12:00-13:00">12:00-13:00</option>
              <option value="13:00-14:00">13:00-14:00</option>
              <option value="14:00-15:00">14:00-15:00</option>
              <option value="15:00-16:00">15:00-16:00</option>
              <option value="16:00-17:00">16:00-17:00</option>
              <option value="17:00-18:00">17:00-18:00</option>
              <option value="18:00-19:00">18:00-19:00</option>
              <option value="19:00-20:00">19:00-20:00</option>
              <option value="20:00-21:00">20:00-21:00</option>
              <option value="21:00-22:00">21:00-22:00</option>
            </select>
          </div>

          {actionPlan?.steps && actionPlan.steps.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Étape du plan d'action
              </label>
              <select
                value={formData.stepId}
                onChange={(e) => setFormData({ ...formData, stepId: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
              >
                <option value="">Aucune étape liée</option>
                {actionPlan.steps.map(step => (
                  <option key={step.id} value={step.id}>
                    {step.titre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Calendar className="h-5 w-5 text-purple-600 mr-2" />
              <h4 className="font-medium text-purple-800">Aperçu de la planification</h4>
            </div>
            <div className="text-sm text-purple-700 space-y-1">
              <p><strong>Date :</strong> {selectedDate.toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long',
                year: 'numeric'
              })}</p>
              <p><strong>Horaire :</strong> {formData.timeSlot}</p>
              <p><strong>Durée :</strong> {formData.duration}</p>
              <p><strong>Priorité :</strong> {
                formData.priority === 'high' ? 'Haute' :
                formData.priority === 'medium' ? 'Moyenne' : 'Basse'
              }</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
            >
              {isEditing ? 'Modifier l\'action' : 'Créer l\'action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanningActionManager;